import gradeService from "@/modules/ReportCard/Grades/service";
import termService from "@/modules/ReportCard/Terms/service";
import subjectService from "@/modules/ReportCard/Subject/service";
import curriculumService from "@/modules/ReportCard/Curriculums/service.js";
import termCommentService from "@/modules/ReportCard/TermComments/service";
import studentService from "@/modules/Student/service";
import termMaterialsService from "@/modules/TermMaterials/service.js";


/**
 * Convert image URL → base64 (used by pdfMake)
 */
// async function logoToBase64(imageUrl) {
//   if (!imageUrl) return null;
//   const response = await fetch(imageUrl, { mode: "cors" });
//   const blob = await response.blob();
//   return new Promise((resolve) => {
//     const reader = new FileReader();
//     reader.onloadend = () => resolve(reader.result);
//     reader.readAsDataURL(blob);
//   });
// }

function parseOrdinalToNumber(posText) {
  if (!posText || posText === "N/A") return null;
  const n = parseInt(String(posText).replace(/\D/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

function pickTaleemAIRemarks(summaries, studentName = "") {
  if (!Array.isArray(summaries) || !summaries.length) {
    return "Keep a consistent routine for revision and practice. Small daily effort brings big improvement.";
  }

  const clean = summaries
    .map((s) => ({
      name: s?.name || "Curriculum",
      positionNum: Number.isFinite(s?.positionNum) ? s.positionNum : null,
      percentage: Number.isFinite(s?.percentage) ? s.percentage : null,
      weakSubjects: Array.isArray(s?.weakSubjects) ? s.weakSubjects.filter(Boolean) : [],
    }))
    .filter(Boolean);

  const count = clean.length;

  // pick BEST curriculum (lowest position number)
  const best =
    clean
      .filter((s) => s.positionNum !== null)
      .sort((a, b) => a.positionNum - b.positionNum)[0] ||
    clean
      .filter((s) => s.percentage !== null)
      .sort((a, b) => b.percentage - a.percentage)[0] ||
    clean[0];

  // pick WORST curriculum (highest position number)
  const worst =
    clean
      .filter((s) => s.positionNum !== null)
      .sort((a, b) => b.positionNum - a.positionNum)[0] ||
    clean
      .filter((s) => s.percentage !== null)
      .sort((a, b) => a.percentage - b.percentage)[0] ||
    clean[clean.length - 1];

  // Tone based on BEST result
  let tone = "Good effort, with room to grow.";
  if (best?.positionNum === 1) tone = "MashaAllah, excellent overall performance.";
  else if (best?.positionNum === 2) tone = "Great work, keep it up.";
  else if (best?.positionNum === 3) tone = "Strong effort and good consistency.";
  else if (best?.positionNum !== null && best.positionNum >= 7)
    tone = "Alhamdulillah, keep going and stay consistent.";

  const introName = studentName ? `${studentName}, ` : "";

  // Focus areas should come from WORST curriculum
  let focusAreas = (worst?.weakSubjects || []).slice(0, 2);

  // fallback if worst has no focus subjects
  if (!focusAreas.length) {
    focusAreas = clean.flatMap((s) => s.weakSubjects || []).filter(Boolean).slice(0, 2);
  }

  // ✅ SINGLE CURRICULUM: do NOT mention curriculum name at all
  if (count === 1) {
    const strengthLine = "Good progress in class overall.";
    const improvementLine = focusAreas.length
      ? `To improve further, focus more on ${focusAreas.join(", ")}.`
      : "To improve further, focus on regular revision and correcting mistakes early.";
    const closingLine = "Regular revision and consistent practice will help strengthen these areas.";

    return [tone, `${introName}${strengthLine}`.trim(), improvementLine, closingLine]
      .filter(Boolean)
      .join(" ");
  }

  // ✅ MULTIPLE CURRICULUMS: mention best + worst curriculum names
  const strengthLine = best?.name ? `You did very well in ${best.name}.` : "";
  const improvementLine = focusAreas.length
    ? `To improve further, focus more on ${worst?.name || "the weaker area"}, especially ${focusAreas.join(", ")}.`
    : "To improve further, focus on regular revision and correcting mistakes early.";
  const closingLine = "Regular revision and consistent practice will help strengthen these areas.";

  return [tone, `${introName}${strengthLine}`.trim(), improvementLine, closingLine]
    .filter(Boolean)
    .join(" ");
}

async function logoToBase64(imageUrl) {
  if (!imageUrl) return null;
  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.log("Logo fetch blocked (CORS). Generating report without logo.", e);
    return null;
  }
}

function formatOrdinal(num) {
  if (!num) return "N/A";
  const n = parseInt(num, 10);
  if ([11, 12, 13].includes(n % 100)) return `${n}th`;
  if (n % 10 === 1) return `${n}st`;
  if (n % 10 === 2) return `${n}nd`;
  if (n % 10 === 3) return `${n}rd`;
  return `${n}th`;
}

function getStudentId(student) {
  if (!student) return null;
  return student.StudentId || student.id || null;
}

function getStudentName(student) {
  if (!student) return "N/A";
  return (
    student.fullName ||
    (student.Student && (student.Student.fullName || student.Student.name)) ||
    student.name ||
    "N/A"
  );
}

// function getGradeLetter(percent) {
//   if (percent >= 90) return "A";
//   if (percent >= 80) return "B";
//   if (percent >= 70) return "C";
//   if (percent >= 60) return "D";
//   return "F";
// }

function getCurriculumSubjectsMeta(curriculum) {
  if (!curriculum || !curriculum.subjectMeta) return [];
  const subjects = curriculum.subjectMeta.subjects;
  if (!Array.isArray(subjects)) return [];

  // ✅ sort by saved order (fallback to original)
  return subjects.slice().sort((a, b) => {
    const ao = Number.isFinite(Number(a.order)) ? Number(a.order) : 9999;
    const bo = Number.isFinite(Number(b.order)) ? Number(b.order) : 9999;
    return ao - bo;
  });
}

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export async function buildStudentReportCard({
  student,
  classroom,
  year,
  currentBusiness,
}) {
  const currentStudentId = getStudentId(student);

  /* =========================
     1) Terms
  ========================= */
  const termsRes = await termService.getAll({ YearId: year.id });
  const terms = (termsRes && termsRes.data) ? termsRes.data : [];
  const lastTerm = terms.length ? terms[terms.length - 1] : null;

  /* =========================
     2) Students in classroom (used for ranking)
  ========================= */
  const studentsRes = await studentService.getStudentByClassRoomId({
    ClassRoomId: classroom.id,
  });
  const students = (studentsRes && studentsRes.data) ? studentsRes.data : [];
  const studentIds = students.map((s) => s.StudentId).filter(Boolean);


  /* =========================
     3) Curriculums (supports new + old)
  ========================= */
  let curriculums = [];

  // New relation: classroom.Curriculums
  if (classroom && Array.isArray(classroom.Curriculums) && classroom.Curriculums.length) {
    curriculums = classroom.Curriculums;
  }
  // Fallback: old single CurriculumId
  else if (classroom && classroom.CurriculumId) {
    const curriculumRes = await curriculumService.getById(classroom.CurriculumId);
    if (curriculumRes && curriculumRes.data) curriculums = [curriculumRes.data];
  }

  // If none assigned, return a basic page
  if (!curriculums.length) {
    return [
      { text: "REPORT CARD", alignment: "center", fontSize: 22, bold: true },
      { text: "No curriculums assigned to this classroom.", margin: [0, 20, 0, 0] },
    ];
  }



  /* =========================
     4) Term Comments (once per student)
  ========================= */
  const termCommentsRes = await termCommentService.getAll({
    filteredTerms: terms.map((t) => t.id),
    StudentId: currentStudentId,
  });

  const termCommentRows = (termCommentsRes && termCommentsRes.data) ? termCommentsRes.data : [];

  // ✅ True if ANY term has a non-empty teacher comment
  const hasTeacherRemarks = termCommentRows.some((row) => {
    const c = (row && row.comment) ? String(row.comment).trim() : "";
    return c.length > 0;
  });

  const teacherCommentBlocks = termCommentRows
    .filter((row) => {
      const c = (row && row.comment) ? String(row.comment).trim() : "";
      return c.length > 0;
    })
    .map((row) => {
      const termName = (row.Term && row.Term.name) ? row.Term.name : "Term";
      const comment = String(row.comment || "").trim();

      return {
        termName,
        comment,
      };
    });

  /* =========================
     5) Page Header (once)
  ========================= */
  const page = [];

  const curriculumSummaries = [];

  page.push({ text: "REPORT CARD", alignment: "center", fontSize: 22, bold: true });

  const logo = await logoToBase64(currentBusiness.imageUrl);

  page.push({
    columns: [
      {
        image: logo,
        width: 60,
        alignment: "left",
      },
      {
        stack: [
          { text: currentBusiness.name, bold: true },
          { text: currentBusiness.address },
          { text: currentBusiness.phoneNumber },
          { text: currentBusiness.email },
        ],
        alignment: "right", // optional (keeps the text right-aligned)
        width: "*",
      },
    ],
  });

  page.push({
    margin: [0, 10, 0, 10],
    columns: [
      {
        stack: [
          {
            text: [
              { text: "Name: " },
              { text: getStudentName(student), bold: true },
            ],
          },
        ],
      },
      { text: `Year: ${year.name}`, alignment: "center" },
      {
        text: classroom && classroom.name ? `Classroom: ${classroom.name}` : "",
        alignment: "right",
      },
    ],
  });

  /* =========================
     6) Render each curriculum section
  ========================= */
  for (let cIndex = 0; cIndex < curriculums.length; cIndex++) {
    const curriculum = curriculums[cIndex];
    const metaSubjects = getCurriculumSubjectsMeta(curriculum);

    // Skip empty curriculum but show a note
    if (!metaSubjects.length) {
      page.push({
        text: `${curriculum.name || "Curriculum"} (No subjects assigned)`,
        bold: true,
        margin: [0, 10, 0, 6],
        color: "red",
      });
      continue;
    }

    // Curriculum title
    page.push({
      text: curriculum.name || "Curriculum",
      bold: true,
      fontSize: 14,
      margin: [0, 12, 0, 6],
    });

    // Pull subject details
    const subjectIds = metaSubjects.map((s) => s.id).filter(Boolean);
    const subjectsRes = subjectIds.length
      ? await subjectService.getAll({ filteredSubjects: subjectIds })
      : { data: [] };

    const fetchedSubjects = (subjectsRes && subjectsRes.data) ? subjectsRes.data : [];

    // ✅ reorder fetched subjects to match metaSubjects order
    const subjects = metaSubjects
      .map((m) => fetchedSubjects.find((s) => s.id === m.id))
      .filter(Boolean);

    // ✅ Apply weightage AND keep order
    for (let i = 0; i < metaSubjects.length; i++) {
      const m = metaSubjects[i];
      const s = subjects.find((x) => x.id === m.id);
      if (s) {
        s.weightage = m.marks;
        s.order = m.order; // optional, helps debugging
      }
    }

    /* =========================
       Grades (ALL students, this curriculum)
       - requires backend to support CurriculumId filter
    ========================= */
    const gradesRes = await gradeService.getAll({
      YearId: year.id,
      filteredStudents: studentIds,
      ClassroomId: classroom.id, // keep your existing key to match your current API
      CurriculumId: curriculum.id,
    });

    const allGrades = (gradesRes && gradesRes.data) ? gradesRes.data : [];
    const studentGrades = allGrades.filter((g) => g.StudentId === currentStudentId);

    // console.log('YearId', year)
    // console.log('gradesRes', studentIds)
    // console.log('ClassroomId', classroom)
    // console.log('CurriculumId', curriculum)

    console.log('gradesRes', gradesRes)
    /* =========================
       Build Grade Table
    ========================= */
    const reportCard = [];
    let overallPercentage = 0;

    const headerRow = [{ text: "Subjects", bold: true, alignment: "center" }];
    const labelRow = [{ text: "", bold: true }];

    for (let t = 0; t < terms.length; t++) {
      const term = terms[t];
      headerRow.push({ text: term.name, colSpan: 2, alignment: "center" });
      headerRow.push({});
      labelRow.push({ text: "Grade", alignment: "center" });
      labelRow.push({ text: "%", alignment: "center" });
    }

    reportCard.push(headerRow, labelRow);

    for (let sIndex = 0; sIndex < subjects.length; sIndex++) {
      const subj = subjects[sIndex];
      const weight = safeNumber(subj.weightage);

      const row = [{ text: `${subj.name} (${weight}%)`, noWrap: false }];

      for (let t = 0; t < terms.length; t++) {
        const term = terms[t];

        const g = studentGrades.find(
          (x) => x.SubjectId === subj.id && x.TermId === term.id
        );

        const obtained = g ? safeNumber(g.ObtainedMarks) : 0;
        const totalMarks = g ? safeNumber(g.totalMarks) : 0;

        const weighted = totalMarks
          ? (obtained / totalMarks) * weight
          : 0;

        overallPercentage += weighted;

        row.push({ text: obtained.toFixed(1), alignment: "center" });
        row.push({ text: `${weighted.toFixed(1)}%`, alignment: "center" });
      }

      reportCard.push(row);
    }
    overallPercentage = terms.length ? (overallPercentage / terms.length) : overallPercentage;
    overallPercentage = Number(overallPercentage.toFixed(1));
    // const gradeLetter = getGradeLetter(overallPercentage);
    const passFail = overallPercentage >= 60 ? "Pass" : "Fail";

    /* =========================
       Position (rank) for this curriculum (last term)
       Rank students based on lastTerm totals using this curriculum subjects
    ========================= */
    let classPositionText = "N/A";

    if (lastTerm) {
      const ranked = students
        .map((s) => {
          const sid = s.StudentId;

          const termGrades = allGrades.filter(
            (g) => g.StudentId === sid && g.TermId === lastTerm.id
          );

          if (!termGrades.length) return null;

          const total = termGrades.reduce((sum, g) => {
            const subj = subjects.find((x) => x.id === g.SubjectId);
            if (!subj) return sum;

            const weight = safeNumber(subj.weightage);
            const obtained = safeNumber(g.ObtainedMarks);
            const totalMarks = safeNumber(g.totalMarks);

            if (!totalMarks) return sum;

            return sum + (obtained / totalMarks) * weight;
          }, 0);

          return { studentId: sid, total };
        })
        .filter(Boolean)
        .sort((a, b) => b.total - a.total);

      const idx = ranked.findIndex((r) => r.studentId === currentStudentId);
      if (idx >= 0) classPositionText = formatOrdinal(idx + 1);
    }

    /* =========================
       Curriculum Summary Row
    ========================= */
    page.push({
      margin: [0, 0, 0, 8],
      columns: [
        {
          stack: [
            { text: [{ text: "Overall Percentage: " }, { text: `${overallPercentage}%`, bold: true }] },
            // { text: [{ text: "Grade: " }, { text: gradeLetter, bold: true }] },
            { text: [{ text: "Result: " }, { text: passFail, bold: true }] },
          ],
        },
        { text: `Position: ${classPositionText}`, alignment: "right", bold: true },
      ],
    });

    // Curriculum table
    // page.push({
    //   // table: { widths: "*", body: reportCard },
    //   table: {
    //     widths: [180].concat(terms.flatMap(() => [45, 45])),
    //     body: reportCard,
    //   },
    //   layout: {
    //     hLineWidth(i, node) {
    //       return i === 0 || i === node.table.body.length ? 2 : 1;
    //     },
    //     vLineWidth(i, node) {
    //       return i === 0 || i === node.table.widths.length ? 2 : 1;
    //     },
    //     hLineColor() { return "#000"; },
    //     vLineColor() { return "#000"; },
    //   },
    // });

    page.push({
      table: {
        // ✅ makes table use full available page width
        // Subjects column stretches, term columns stay small
        widths: ["*"].concat(terms.flatMap(() => [45, 45])),
        body: reportCard,
      },
      layout: {
        hLineWidth(i, node) {
          return i === 0 || i === node.table.body.length ? 2 : 1;
        },
        vLineWidth(i, node) {
          return i === 0 || i === node.table.widths.length ? 2 : 1;
        },
        hLineColor() { return "#000"; },
        vLineColor() { return "#000"; },
      },
    });

    // Spacer between curriculums
    if (cIndex < curriculums.length - 1) {
      page.push({ text: "", margin: [0, 10, 0, 0] });
    }
    // Find weakest subjects for this curriculum (lowest avg)
    // Find focus areas for this curriculum (by LOST points, not small-weight subjects)
    let weakSubjects = [];
    let missingSubjects = [];

    try {
      const perSubject = subjects.map((subj) => {
        const weight = safeNumber(subj.weightage);

        let totalObtained = 0;
        let totalPossible = 0;

        for (let t = 0; t < terms.length; t++) {
          const term = terms[t];
          const g = studentGrades.find(
            (x) => x.SubjectId === subj.id && x.TermId === term.id
          );

          const obtained = g ? safeNumber(g.ObtainedMarks) : 0;
          const totalMarks = g ? safeNumber(g.totalMarks) : 0;

          // If totalMarks is 0, treat as missing for that term
          totalObtained += obtained;
          totalPossible += totalMarks;
        }

        // Missing grades (no total possible across terms)
        if (!totalPossible) {
          return { name: subj.name, missing: true, lost: 0 };
        }

        const ratio = totalObtained / totalPossible; // 0..1

        // ✅ If student is already 95%+ in this subject, don't treat it as a focus area
        if (ratio >= 0.95) {
          return { name: subj.name, missing: false, lost: 0 };
        }

        const lost = (1 - ratio) * weight; // weighted points lost
        return { name: subj.name, missing: false, lost };
      });

      missingSubjects = perSubject
        .filter((x) => x.missing)
        .map((x) => x.name);

      weakSubjects = perSubject
        .filter((x) => !x.missing)
        .sort((a, b) => b.lost - a.lost) // MOST lost points first
        .slice(0, 2)
        .map((x) => x.name);

      // Put missing first, then weak
      weakSubjects = missingSubjects.concat(weakSubjects).slice(0, 3);
    } catch (e) {
      weakSubjects = [];
    }

    curriculumSummaries.push({
      name: curriculum.name || "Curriculum",
      positionText: classPositionText,
      positionNum: parseOrdinalToNumber(classPositionText),
      percentage: overallPercentage,
      passFail,
      weakSubjects,
    });

  }

  /* =========================
   Materials Covered (GRID like subjects)
   Rows = questions
   Cols = terms
========================= */

  // 1) Fetch all term material forms in parallel
  const termForms = await Promise.all(
    (terms || []).map(async (term) => {
      try {
        const formRes = await termMaterialsService.getForm({
          ClassRoomId: classroom.id,
          TermId: term.id,
          StudentId: currentStudentId,
        });

        // your service usually returns { data: {...} }
        const data = formRes?.data || formRes || {};
        return {
          termId: term.id,
          termName: term.name,
          template: data.template || null,
          responses: data.responses || {},
        };
      } catch (e) {
        // if API fails for a term, keep empty so grid still renders
        return {
          termId: term.id,
          termName: term.name,
          template: null,
          responses: {},
        };
      }
    })
  );

  // 2) Decide which template to use
  // Prefer the first term that has a template.
  // If templates vary, we build a union of fields by key.
  const fieldsByKey = new Map();

  termForms.forEach((tf) => {
    const fields = Array.isArray(tf?.template?.fields) ? tf.template.fields : [];
    fields.forEach((f, idx) => {
      if (!f?.key) return;
      if (!fieldsByKey.has(f.key)) {
        fieldsByKey.set(f.key, {
          key: f.key,
          label: f.label || f.key,
          required: !!f.required,
          __order: idx,
        });
      }
    });
  });

  const materialFields = Array.from(fieldsByKey.values())
    // keep stable ordering based on first seen index
    .sort((a, b) => (a.__order || 0) - (b.__order || 0));

  // 3) Build table only if we have fields
  if (materialFields.length) {
    // Header row (like subjects)
    const headerRow = [
      { text: "Materials Covered", bold: true, alignment: "center" },
    ];

    // One column per term
    termForms.forEach((tf) => {
      headerRow.push({ text: tf.termName || "", alignment: "center", bold: true });
    });

    const body = [headerRow];

    // Optional 2nd header row (small label row)
    // const labelRow = [{ text: "", bold: true }];
    // termForms.forEach(() => labelRow.push({ text: "Covered", alignment: "center" }));
    // body.push(labelRow);

    // Rows (one per question)
    materialFields.forEach((q) => {
      const row = [
        {
          text: q.required ? `${q.label} *` : q.label,
          bold: true,
        },
      ];

      termForms.forEach((tf) => {
        const val = tf?.responses ? tf.responses[q.key] : "";
        const text =
          val === null || val === undefined ? "" : String(val).trim();

        row.push({
          text,
          alignment: "left",
        });
      });

      body.push(row);
    });

    // Render it
    page.push({ text: "", margin: [0, 12, 0, 0] });
    page.push({
      table: {
        // first col wider, remaining cols share space
        widths: [180].concat(termForms.map(() => "*")),
        body,
      },
      layout: {
        hLineWidth(i, node) {
          return i === 0 || i === node.table.body.length ? 2 : 1;
        },
        vLineWidth(i, node) {
          return i === 0 || i === node.table.widths.length ? 2 : 1;
        },
        hLineColor() { return "#000"; },
        vLineColor() { return "#000"; },
      },
    });
  }

  /* =========================
     7) Term Comments (once at end)
  ========================= */
  if (teacherCommentBlocks.length) {
    // small spacer before teacher comments
    page.push({ text: "", margin: [0, 10, 0, 0] });

    teacherCommentBlocks.forEach((block, idx) => {
      page.push({
        table: {
          widths: ["*"],
          body: [
            [
              {
                stack: [
                  { text: `${block.termName} Comments`, bold: true, fontSize: 11, margin: [0, 0, 0, 4] },
                  { text: block.comment, fontSize: 9.5, lineHeight: 1.25 },
                ],
                margin: [8, 6, 8, 6],
              },
            ],
          ],
        },
        layout: {
          hLineWidth() { return 1; },
          vLineWidth() { return 1; },
          hLineColor() { return "#000"; },
          vLineColor() { return "#000"; },
        },
      });

      // spacing between term blocks (but not after the last)
      if (idx < teacherCommentBlocks.length - 1) {
        page.push({ text: "", margin: [0, 6, 0, 0] });
      }
    });
  }

  // ✅ Only show Taleem remarks if there are NO teacher remarks at all
  if (!hasTeacherRemarks) {
    const taleemRemark = pickTaleemAIRemarks(curriculumSummaries, getStudentName(student));

    page.push({ text: "", margin: [0, 14, 0, 0] });

    page.push({
      table: {
        widths: ["*"],
        body: [
          [
            {
              stack: [
                { text: "Remarks from Taleem", bold: true, fontSize: 11, margin: [0, 0, 0, 4] },
                { text: taleemRemark, fontSize: 10, lineHeight: 1.2 },
              ],
              margin: [8, 6, 8, 6],
            },
          ],
        ],
      },
      layout: {
        hLineWidth() { return 1; },
        vLineWidth() { return 1; },
        hLineColor() { return "#000"; },
        vLineColor() { return "#000"; },
      },
    });
  }

  /* =========================
     8) Signatures
  ========================= */
  page.push({
    columns: [
      { text: "Teacher Signature", alignment: "left" },
      { text: "Supervisor Signature", alignment: "right" },
    ],
    margin: [0, 30, 0, 0],
  });

  return page;
}